// Translations. The English text is the key: t('Profit target'), t('{n} orders placed', { n: 5 }).
// Other languages live in web/locales/<code>.json ({ "English text": "translation" }) and load on demand.
// Missing translations fall back to English, so the interface never shows blanks.
import { useEffect, useState } from 'react';

export const LANGS = [
  { code: 'en', name: 'English', locale: 'en-US' },
  { code: 'es', name: 'Español', locale: 'es-ES' },
  { code: 'pt', name: 'Português (Brasil)', locale: 'pt-BR' },
  { code: 'ru', name: 'Русский', locale: 'ru-RU' },
  { code: 'tr', name: 'Türkçe', locale: 'tr-TR' },
  { code: 'vi', name: 'Tiếng Việt', locale: 'vi-VN' },
  { code: 'th', name: 'ไทย', locale: 'th-TH' },
  { code: 'id', name: 'Bahasa Indonesia', locale: 'id-ID' },
  { code: 'ar', name: 'العربية', locale: 'ar', rtl: true },
  { code: 'he', name: 'עברית', locale: 'he-IL', rtl: true },
  { code: 'zh', name: '简体中文', locale: 'zh-CN' },
  { code: 'ja', name: '日本語', locale: 'ja-JP' },
  { code: 'ko', name: '한국어', locale: 'ko-KR' },
];

let lang = 'en';
let dict = {};
const fill = (s, vars) => (vars ? s.replace(/\{(\w+)\}/g, (m, k) => (vars[k] == null ? m : String(vars[k]))) : s);

// tk marks text for translation where it is defined (e.g. constant tables); translate at display time with t(value).
export const tk = (text) => text;

export function t(text, vars) {
  const tr = dict[text];
  return fill(typeof tr === 'string' && tr ? tr : text, vars);
}

// Server error messages arrive in English; translate known ones (with numbers) where possible.
const ERR_PATTERNS = [
  [/^Minimum order size is \$([\d.,]+)$/, 'Minimum order size is ${n}'],
  [/^Minimum size for (.+) is ([\d.e-]+)$/, 'Minimum size for {market} is {n}'],
  [/^Minimum close size is ([\d.e-]+)$/, 'Minimum close size is {n}'],
  [/^Leverage for (.+) must be between 1 and (\d+)$/, 'Leverage for {market} must be between 1 and {n}'],
  [/^Insufficient margin: needs \$([\d.,]+), equity \$([\d.,-]+)$/, 'Insufficient margin: needs ${n}, equity ${m}'],
  [/^Position too large: max \$([\d.,]+) margin per position \((\d+)% of the account\)$/, 'Position too large: max ${n} margin per position ({m}% of the account)'],
  [/^Maximum (\d+) open positions per account$/, 'Maximum {n} open positions per account'],
  [/^Maximum (\d+) open orders per account \(you have (\d+)\)$/, 'Maximum {n} open orders per account (you have {m})'],
  [/^Number of orders must be between 2 and (\d+)$/, 'Number of orders must be between 2 and {n}'],
  [/^Each order must be at least \$([\d.,]+)\. Increase the total size or use fewer orders\.$/, 'Each order must be at least ${n}. Increase the total size or use fewer orders.'],
  [/^For a scaled buy, all prices must be below the current price \(([\d.,]+)\)$/, 'For a scaled buy, all prices must be below the current price ({n})'],
  [/^For a scaled sell, all prices must be above the current price \(([\d.,]+)\)$/, 'For a scaled sell, all prices must be above the current price ({n})'],
  [/^Stop-loss for a long must be below ([\d.,]+)$/, 'Stop-loss for a long must be below {n}'],
  [/^Stop-loss for a short must be above ([\d.,]+)$/, 'Stop-loss for a short must be above {n}'],
  [/^Take-profit for a long must be above ([\d.,]+)$/, 'Take-profit for a long must be above {n}'],
  [/^Take-profit for a short must be below ([\d.,]+)$/, 'Take-profit for a short must be below {n}'],
  [/^Live price unavailable for (\w+); try again shortly$/, 'Live price unavailable for {market}; try again shortly'],
  [/^No open position for (\w+)$/, 'No open position for {market}'],
  [/^Request failed \((\d+)\)$/, 'Request failed ({n})'],
  [/^Account is (\w+); trading is disabled$/, 'Account is {n}; trading is disabled'],
];
export function tErr(msg) {
  if (!msg) return t('Something went wrong. Please try again.');
  if (dict[msg]) return dict[msg];
  for (const [re, key] of ERR_PATTERNS) {
    const m = String(msg).match(re);
    if (m) {
      const names = [...key.matchAll(/\{(\w+)\}/g)].map((x) => x[1]);
      const vars = {}; names.forEach((n, i) => { vars[n] = m[i + 1]; });
      return t(key, vars);
    }
  }
  return msg;
}

export const getLang = () => lang;
export const langInfo = (code = lang) => LANGS.find((l) => l.code === code) || LANGS[0];
export const dateLocale = () => langInfo().locale;
export const fmtDateTime = (d) => new Date(d).toLocaleString(dateLocale());
export const fmtTime = (d) => new Date(d).toLocaleTimeString(dateLocale());
export const fmtDate = (d) => new Date(d).toLocaleDateString(dateLocale());

export function detectLang() {
  try { const saved = localStorage.getItem('lang'); if (saved && LANGS.some((l) => l.code === saved)) return saved; } catch { /* ignore */ }
  const prefs = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || 'en']).map((x) => String(x).toLowerCase());
  for (const p of prefs) {
    const base = p.split('-')[0] === 'iw' ? 'he' : p.split('-')[0];
    if (LANGS.some((l) => l.code === base)) return base;
  }
  return 'en';
}

export async function loadLang(code, { remember = false } = {}) {
  if (!LANGS.some((l) => l.code === code)) code = 'en';
  let next = {};
  if (code !== 'en') {
    try {
      const r = await fetch(`/locales/${code}.json`, { cache: 'no-cache' });
      if (r.ok) next = await r.json();
    } catch { /* fall back to English text */ }
  }
  lang = code; dict = next;
  const info = langInfo(code);
  document.documentElement.lang = code;
  document.documentElement.dir = info.rtl ? 'rtl' : 'ltr';
  if (remember) { try { localStorage.setItem('lang', code); } catch { /* ignore */ } }
  window.dispatchEvent(new CustomEvent('fx:lang', { detail: code }));
  return code;
}

export function useLang() {
  const [l, set] = useState(lang);
  useEffect(() => {
    const f = (e) => set(e.detail);
    window.addEventListener('fx:lang', f);
    return () => window.removeEventListener('fx:lang', f);
  }, []);
  return l;
}
